/**
 * ProtectedRoute Component
 *
 * Route protection components for wouter navigation.
 * Inlines guard logic to avoid re-creating component types on every render
 * (which would cause infinite re-render loops with useEffect + setState).
 *
 * Uses AuthContext for authentication state.
 *
 * Ported from journey-web's guard pattern, adapted for business-web:
 * - No requireJourneyInstance (business-web doesn't need it)
 * - Roles: business_admin, business_user, viewer
 */

import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/core/contexts/AuthContext';
import { LoadingSpinner } from '@/components/shell/LoadingSpinner';
import { BusinessPortalGuard } from '@/components/guards/BusinessPortalGuard';

// ═══════════════════════════════════════════════════════════════════════
// LOADING FALLBACK
// ═══════════════════════════════════════════════════════════════════════

const LoadingFallback = () => <LoadingSpinner />;

// ═══════════════════════════════════════════════════════════════════════
// GUARD EVALUATION
// ═══════════════════════════════════════════════════════════════════════

interface GuardResult {
  allowed: boolean;
  redirectTo?: string;
  reason?: string;
}

interface GuardUser {
  id: string;
  email: string;
  role: string;
  permissions: string[];
}

type BusinessRole = 'business_admin' | 'business_user' | 'viewer';

function evaluateGuards(
  isAuthenticated: boolean,
  user: GuardUser | undefined,
  options: {
    requireAuth: boolean;
    requireRole?: BusinessRole[];
    requirePermissions?: string[];
    redirectTo: string;
  }
): GuardResult {
  const { requireAuth, requireRole, requirePermissions, redirectTo } = options;

  // Check authentication requirement
  if (requireAuth && !isAuthenticated) {
    return { allowed: false, redirectTo, reason: 'User not authenticated' };
  }

  // Check role requirement
  if (requireRole && requireRole.length > 0 && user) {
    const userRole = user.role || 'business_user';
    if (!requireRole.includes(userRole as BusinessRole)) {
      return {
        allowed: false,
        redirectTo: '/dashboard',
        reason: `User role '${userRole}' not in required roles: ${requireRole.join(', ')}`,
      };
    }
  }

  // Check permissions requirement
  if (requirePermissions && requirePermissions.length > 0 && user) {
    const userPermissions = user.permissions || [];
    const hasAllPermissions = requirePermissions.every((p) => userPermissions.includes(p));
    if (!hasAllPermissions) {
      return {
        allowed: false,
        redirectTo: '/dashboard',
        reason: `User missing required permissions: ${requirePermissions.join(', ')}`,
      };
    }
  }

  return { allowed: true };
}

// ═══════════════════════════════════════════════════════════════════════
// PROTECTED ROUTE
// ═══════════════════════════════════════════════════════════════════════

/**
 * ProtectedRoute - Route protection wrapper
 *
 * Evaluates auth guards and redirects if conditions are not met.
 * Uses stable primitive dependencies to avoid infinite re-render loops.
 */
export function ProtectedRoute({
  children,
  requireAuth = true,
  requireRole,
  requirePermissions,
  redirectTo = '/login',
  fallback,
}: {
  children: ReactNode;
  requireAuth?: boolean;
  requireRole?: BusinessRole[];
  requirePermissions?: string[];
  redirectTo?: string;
  fallback?: ReactNode;
}) {
  const { user, isLoading, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();
  const [guardResult, setGuardResult] = useState<GuardResult | null>(null);
  const hasRedirected = useRef(false);

  // Stabilize setLocation via ref so it never triggers effect re-runs
  const setLocationRef = useRef(setLocation);
  setLocationRef.current = setLocation;
  const navigate = useCallback((to: string) => setLocationRef.current(to), []);

  // Build a stable user object for guard evaluation — memoized on primitive values
  const guardUser = useMemo<GuardUser | undefined>(() => {
    if (!user) return undefined;
    return {
      id: user.id,
      email: user.email,
      role: user.role || 'business_user',
      permissions: user.permissions || [],
    };
  }, [user]);

  // Stabilize requireRole reference (array identity changes every render)
  const requireRoleKey = requireRole?.join(',') ?? '';
  const requirePermissionsKey = requirePermissions?.join(',') ?? '';

  useEffect(() => {
    if (isLoading) return;

    const result = evaluateGuards(isAuthenticated, guardUser, {
      requireAuth,
      requireRole,
      requirePermissions,
      redirectTo,
    });

    setGuardResult(result);

    if (!result.allowed && result.redirectTo && !hasRedirected.current) {
      hasRedirected.current = true;
      // Store current path for redirect after login (skip if user is actively logging out)
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        if (currentPath !== '/' && currentPath !== redirectTo) {
          sessionStorage.setItem('tonnie_redirect_after_auth', currentPath);
        }
      }
      navigate(result.redirectTo);
    }
  }, [
    isLoading,
    isAuthenticated,
    guardUser,
    requireAuth,
    requireRoleKey,
    requirePermissionsKey,
    redirectTo,
    navigate,
  ]);

  // Show loading while checking auth
  if (isLoading) {
    return <>{fallback || <LoadingFallback />}</>;
  }

  // Show loading while evaluating guards (first render)
  if (guardResult === null) {
    return <>{fallback || <LoadingFallback />}</>;
  }

  // Access denied
  if (!guardResult.allowed) {
    return <>{fallback || <LoadingFallback />}</>;
  }

  // Access granted
  return <>{children}</>;
}

// ═══════════════════════════════════════════════════════════════════════
// PUBLIC ONLY ROUTE
// ═══════════════════════════════════════════════════════════════════════

/**
 * PublicOnlyRoute - For routes accessible only when NOT logged in
 *
 * Redirects authenticated users to /dashboard (or stored redirect path).
 */
export function PublicOnlyRoute({
  children,
  redirectTo = '/dashboard',
}: {
  children: ReactNode;
  redirectTo?: string;
}) {
  const { isLoading, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();
  const hasRedirected = useRef(false);

  // Stabilize setLocation via ref so it never triggers effect re-runs
  const setLocationRef = useRef(setLocation);
  setLocationRef.current = setLocation;
  const navigate = useCallback((to: string) => setLocationRef.current(to), []);

  useEffect(() => {
    if (isLoading) return;

    if (isAuthenticated && !hasRedirected.current) {
      hasRedirected.current = true;
      // Check for stored redirect
      const storedRedirect = sessionStorage.getItem('tonnie_redirect_after_auth');
      if (storedRedirect) {
        sessionStorage.removeItem('tonnie_redirect_after_auth');
        navigate(storedRedirect);
      } else {
        navigate(redirectTo);
      }
    }
  }, [isLoading, isAuthenticated, redirectTo, navigate]);

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (isAuthenticated) {
    return <LoadingFallback />;
  }

  return <>{children}</>;
}

// ═══════════════════════════════════════════════════════════════════════
// CONVENIENCE WRAPPERS
// ═══════════════════════════════════════════════════════════════════════

/**
 * AdminRoute — Blocks invited Team Members (`business_user`) from admin-only
 * pages (Billing, Messages, etc.). Anyone without an explicit
 * `business_user` role sees these — this matches reality: only invited team
 * members are ever assigned `business_user`, so legacy admins with
 * undefined / `citizen` / `business_admin` roles all keep their access.
 *
 * Implemented as a separate component (not via `requireRole`) because guard
 * role checking uses a whitelist, and maintaining the whitelist of every
 * legacy admin role is brittle.
 */
export function AdminRoute({ children, fallback }: { children: ReactNode; fallback?: ReactNode }) {
  const { user, isLoading, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();
  const hasRedirected = useRef(false);

  const setLocationRef = useRef(setLocation);
  setLocationRef.current = setLocation;
  const navigate = useCallback((to: string) => setLocationRef.current(to), []);

  const isTeamMember = user?.role === 'business_user';
  const shouldRedirect = !isLoading && (!isAuthenticated || isTeamMember);

  useEffect(() => {
    if (shouldRedirect && !hasRedirected.current) {
      hasRedirected.current = true;
      navigate(isAuthenticated ? '/dashboard' : '/login');
    }
  }, [shouldRedirect, isAuthenticated, navigate]);

  if (isLoading) return <>{fallback || <LoadingFallback />}</>;
  if (shouldRedirect) return <>{fallback || <LoadingFallback />}</>;
  // Admin routes always imply business-portal access. Wrap here so any
  // existing `<AdminRoute>` callsite gets portal gating for free.
  return <BusinessPortalGuard>{children}</BusinessPortalGuard>;
}
